import { ol_Map, ol_MapBrowserEvent, ol_MapBrowserPointerEvent, ol_Overlay } from "@omnia/exports/openlayers"
import { DatalayersMap, DatalayerZone, defineDirective, FieldViewModel, IDatalayerViewerFactory, IOmniaShell, LayerViewModel, omniaResolve, showLayers } from "../omnia"
import { IDatalayerMapDependencies } from "../types"
import { getSelectableLayers, SelectableLayer } from "./layers"
import { CompareLayersPopupArguments, InputLayer } from "./types"

interface ComparerMap {

    map: DatalayersMap
    pointerWatcher: () => void
    pointerOverlay: ol_Overlay

    selection?: SelectableLayer

    layer: LayerViewModel
    layerName: string

    description: string

    viewer: IDatalayerViewerFactory
    viewers: IDatalayerViewerFactory[]

    zone: DatalayerZone

}

export class ComparePopup extends omnia.shell.Dialog {

    static create(): ng.IDirective {

        return defineDirective(ComparePopup, "app/datalayers/compare/cmp.html")

    }

    public field: FieldViewModel
    private fields: FieldViewModel[]

    layers: SelectableLayer[]

    public maps: ComparerMap[]

    public enableSelection = false
    public showZoneCount = true

    static $inject = ["$scope", "OmniaShell", "OmniaDatalayersDependencies"]

    constructor(
        $scope: ng.IScope,
        shell: IOmniaShell,
        private readonly mapCtxt: IDatalayerMapDependencies,
        private readonly viewManager = mapCtxt.viewManager) {

        super($scope, shell)

    }

    $onInit() {

        const args = this.arguments as CompareLayersPopupArguments

        const { fieldId, fieldLayers, croppingId, compare } = args

        this.setupField(fieldId, croppingId)

        this.layers = getSelectableLayers(fieldLayers, fieldId)

        this.createMaps()

        if (!compare) {
            this.enableSelection = true
            this.showZoneCount = false
        }
        else {
            this.showLayer(this.maps[0], compare.layer1)
            this.showLayer(this.maps[1], compare.layer2)
        }

    }

    $onDestroy(): void {

        for (const cm of this.maps) {
            cm.pointerWatcher?.()
            cm.map.dispose()
        }
    }

    onLayerSelected(map: ComparerMap) {

        const s = map.selection?.layer

        this.showLayer(map, s)

        if (map.layer) {
            (map.layer.ensureLayerLoaded(true) ?? omniaResolve()).then(() => this.updateLayerInfo())
        }

    }

    onViewerSelected(map: ComparerMap) {

        if (map.layer && map.viewer) {
            map.layer.setViewer(map.viewer)
        }

    }

    onPointerMove(map: ComparerMap, event: ol_MapBrowserPointerEvent) {

        const other = this.maps[0] === map
            ? this.maps[1]
            : this.maps[0]

        const overlayCoordinate = event.coordinate

        other.pointerOverlay.setPosition(overlayCoordinate)

        if (overlayCoordinate) {
            const [x, y] = other.map.getMap().getPixelFromCoordinate(overlayCoordinate)
            other.map.map.showToolTipAtScreenPosition(x, y)
        }
        else {
            other.map.clearTooltip()

        }

        map.pointerOverlay.setPosition(undefined)
    }

    private createMaps() {

        const left = this.createMap()
        const right = this.createMap()

        this.maps = [
            left,
            right
        ]

        right.map.getMap().setView(left.map.getView())

    }

    private createMap(): ComparerMap {

        const map = new DatalayersMap(this.mapCtxt, { viewDefaultExtent: false })

        map.map.tooltipDelay = 0
        const olmap = map.getMap()

        map.viewExtentForDataItem(this.field)

        const pointerOverlay = createPointerOverlay(olmap)

        const cm = {
            map,
            pointerOverlay,
            pointerWatcher: undefined,
            layer: undefined,
            layerName: undefined,
            viewer: undefined,
            viewers: undefined,
            description: undefined,
            zone: undefined
        }

        cm.pointerWatcher = omnia.map.observeOpenlayersObject(olmap, "pointermove", (event: ol_MapBrowserEvent) => this.onPointerMove(cm, event))

        return cm

    }

    private setupField(fieldId: number, croppingId: number) {

        const fieldSvc = this.mapCtxt.fieldSvc
        const field = fieldSvc.findField(fieldId)
        const cropping = croppingId ? field.croppings.filter(c => c.croppingId == croppingId) : field.croppings

        this.field = field

        this.fields = [field].concat(cropping).filter(i => !!i)

    }


    private showLayer(target: ComparerMap, { layer, viewer, name }: InputLayer) {

        const { map } = target

        target.layer?.dispose()

        target.layer = undefined
        target.layerName = undefined

        if (layer) {

            const ctxt = this.mapCtxt.createContext(() => [])
            const vm = new LayerViewModel(ctxt, map, layer)

            if (viewer) {
                vm.setViewer(viewer)
            }

            showLayers(ctxt, [vm], () => {

                map.viewExtent(vm.getExtent())

                map.fieldsLayer.highlight(this.fields)
                map.croppingLayer.highlight(this.fields)

                vm.selectable = true
                map.setSelectableLayers([vm.mapLayer])

                map.watchSelection(() => this.updateLayerInfo())

            })

            target.layer = vm
            target.layerName = name

            target.viewer = viewer ?? this.viewManager.getCurrentViewer(vm.typeId)

            const viewers = this.viewManager.getViewers(vm.typeId) ?? []

            target.viewers = viewers.length <= 1
                ? undefined
                : viewers

        }

        return target

    }

    private updateLayerInfo() {

        for (const cm of this.maps) {
            updateLayerDescription(cm, this.showZoneCount)
            updateSelectedZone(cm)
        }

    }

}

function updateLayerDescription(cm: ComparerMap, showZoneCount: boolean) {

    const layer = cm.layer

    if (!layer?.zonesLoaded) {
        cm.description = undefined
        return
    }

    let d = cm.layerName

    if (showZoneCount) {
        d = `${d} (${layer.datalayer.Zones.length} zones)`
    }

    cm.description = d

}

function updateSelectedZone(cm: ComparerMap) {

    const map = cm.map

    const selection = (map.getSelectedFeatures() || [])
        .map(f => f.get("data"))
        .filter(i => i instanceof DatalayerZone)
        .map(i => <DatalayerZone>i)

    cm.zone = selection[0]

}

function createPointerOverlay(map: ol_Map) {

    const element = document.createElement("div")
    element.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <path d="M3 2L4 21L9 16L12 22L14 21L11 15L16 14Z" fill="white" stroke="black" stroke-width="0.5" stroke-linejoin="round"/>
</svg>`

    const overlay = new ol_Overlay({
        positioning: "top-left",
        offset: [-2, 0],
        position: undefined,
        element
    })

    map.addOverlay(overlay)

    return overlay

}
